import "./midi-player.css";

import { Dropdown, type DropdownOption } from "./dropdown.ts";
import { MidiPlayer } from "../preset/midi-player.ts";
import type { MidiClipEntry } from "../preset/midi-clips.ts";

// MIDI player widget — owns the clip dropdown, hidden file picker, and
// play/stop transport button that live in the synth header.
//
// Special dropdown values:
//   `__none__`   — no clip selected (placeholder).
//   `__upload__` — opens the OS file picker.
//   `bundled:<n>`  — index into the bundled clip manifest.
//   `uploaded:<n>` — index into the session-local uploaded clip list.
//
// Uploaded clips are session-only (no localStorage); the dropdown keeps
// them visible for the rest of the page session in case the user wants
// to switch back after auditioning a bundled clip.

const NONE = "__none__";
const UPLOAD = "__upload__";

export interface MidiPlayerWidgetOptions {
  /** Header slot the widget mounts into. */
  container: HTMLElement;
  player: MidiPlayer;
  clips: MidiClipEntry[];
  /** Surface load / playback errors to the user. */
  onError?: (msg: string) => void;
}

interface UploadedClip {
  name: string;
  bytes: ArrayBuffer;
}

export class MidiPlayerWidget {
  private player: MidiPlayer;
  private clips: MidiClipEntry[];
  private uploads: UploadedClip[] = [];

  private root: HTMLDivElement;
  private dropdownHost: HTMLDivElement;
  private dropdown!: Dropdown<string>;
  private fileInput: HTMLInputElement;
  private transport: HTMLButtonElement;
  private transportGlyph: HTMLSpanElement;

  private currentSelection: string = NONE;
  private clipLoaded = false;
  private onError: ((msg: string) => void) | undefined;

  constructor(opts: MidiPlayerWidgetOptions) {
    this.player = opts.player;
    this.clips = opts.clips;
    if (opts.onError) this.onError = opts.onError;

    this.root = document.createElement("div");
    this.root.className = "synth-boot-midi-player";

    const label = document.createElement("span");
    label.className = "label";
    label.textContent = "clip";
    this.root.append(label);

    this.dropdownHost = document.createElement("div");
    this.root.append(this.dropdownHost);
    this.rebuildDropdown();

    this.transport = document.createElement("button");
    this.transport.type = "button";
    this.transport.className = "midi-transport-btn";
    this.transport.setAttribute("aria-label", "play clip");
    this.transport.disabled = true;
    this.transportGlyph = document.createElement("span");
    this.transportGlyph.className = "glyph";
    this.transport.append(this.transportGlyph);
    this.renderTransportGlyph(false);
    // pointerup (not click) — click is synthesised ~300ms after touchend on
    // some mobile browsers, which can drift outside the user-gesture window
    // needed to also resume the audio context.
    this.transport.addEventListener("pointerup", (ev) => {
      ev.preventDefault();
      this.toggleTransport();
    });
    this.root.append(this.transport);

    this.fileInput = document.createElement("input");
    this.fileInput.type = "file";
    this.fileInput.accept = ".mid,.midi,audio/midi,audio/x-midi";
    this.fileInput.className = "midi-file-input";
    this.fileInput.addEventListener("change", () => void this.handleFileChange());
    this.root.append(this.fileInput);

    opts.container.append(this.root);
  }

  destroy(): void {
    this.player.stop();
    this.dropdown.destroy();
    this.root.remove();
  }

  private rebuildDropdown(): void {
    const options: DropdownOption<string>[] = [
      { value: NONE, label: "(none)" },
      ...this.clips.map((clip, i): DropdownOption<string> => ({
        value: `bundled:${i}`,
        label: clip.name,
      })),
      ...this.uploads.map((up, i): DropdownOption<string> => ({
        value: `uploaded:${i}`,
        label: up.name,
        hint: "uploaded",
      })),
      { value: UPLOAD, label: "upload…" },
    ];

    if (!this.dropdown) {
      this.dropdown = new Dropdown<string>({
        container: this.dropdownHost,
        options,
        value: this.currentSelection,
        placeholder: "select clip…",
        minWidth: 160,
        onChange: (v) => void this.handleSelection(v),
      });
    } else {
      this.dropdown.setOptions(options);
      this.dropdown.setValue(this.currentSelection);
    }
  }

  private async handleSelection(value: string): Promise<void> {
    if (value === UPLOAD) {
      // Don't keep "upload…" as the visible selection; revert the
      // dropdown to whatever was selected before, then trigger the
      // file picker.
      this.dropdown.setValue(this.currentSelection);
      this.fileInput.click();
      return;
    }

    this.currentSelection = value;
    this.player.stop();
    this.clipLoaded = false;
    this.refreshTransport();

    if (value === NONE) return;

    try {
      if (value.startsWith("bundled:")) {
        const idx = Number(value.slice("bundled:".length));
        const clip = this.clips[idx];
        if (!clip) throw new Error("bundled clip out of range");
        await this.player.loadFromUrl(clip.url);
      } else if (value.startsWith("uploaded:")) {
        const idx = Number(value.slice("uploaded:".length));
        const clip = this.uploads[idx];
        if (!clip) throw new Error("uploaded clip out of range");
        this.player.loadFromBytes(clip.bytes);
      }
      this.clipLoaded = true;
      this.refreshTransport();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      this.onError?.(`midi load failed: ${msg}`);
      console.error("[midi-player] load failed:", err);
      this.clipLoaded = false;
      this.refreshTransport();
    }
  }

  private async handleFileChange(): Promise<void> {
    const file = this.fileInput.files?.[0];
    // Reset the input so picking the same file again still fires `change`.
    this.fileInput.value = "";
    if (!file) return;

    let bytes: ArrayBuffer;
    try {
      bytes = await file.arrayBuffer();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      this.onError?.(`could not read file: ${msg}`);
      return;
    }

    this.uploads.push({ name: file.name, bytes });
    const newValue = `uploaded:${this.uploads.length - 1}`;
    this.currentSelection = newValue;
    this.rebuildDropdown();
    void this.handleSelection(newValue);
  }

  private toggleTransport(): void {
    if (!this.clipLoaded) return;
    if (this.player.isPlaying()) {
      this.player.stop();
    } else {
      this.player.play();
    }
    this.refreshTransport();
  }

  private refreshTransport(): void {
    const playing = this.player.isPlaying();
    this.transport.disabled = !this.clipLoaded;
    this.transport.classList.toggle("is-playing", playing);
    this.transport.setAttribute("aria-label", playing ? "stop clip" : "play clip");
    this.renderTransportGlyph(playing);
  }

  private renderTransportGlyph(playing: boolean): void {
    // SVG triangles + square — no emoji, sized to match the button.
    if (playing) {
      this.transportGlyph.innerHTML =
        '<svg viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">' +
        '<rect x="3" y="3" width="8" height="8" rx="1.2" />' +
        '</svg>';
    } else {
      this.transportGlyph.innerHTML =
        '<svg viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">' +
        '<path d="M3.5 2.6 L11.4 7 L3.5 11.4 Z" />' +
        '</svg>';
    }
  }
}
